import React from "react";
import { cn, getSolidBackgroundStyles, getBorderStyles, getFocusRingStyles } from "../../styles";
import { ThemeColor } from "../../props";

export interface CheckboxProps {
  /**
   * Whether the checkbox is in the checked (on) state.
   * @default false
   */
  checked?: boolean;

  /**
   * Callback fired when the checkbox state changes.
   * Receives the new checked state as a boolean parameter.
   */
  onChange?: (checked: boolean) => void;

  /**
   * If true, the checkbox will be disabled and non-interactive.
   * @default false
   */
  disabled?: boolean;

  /**
   * Additional class name(s) to apply to the checkbox element.
   */
  className?: string;

  /**
   * HTML id attribute for the checkbox element.
   */
  id?: string;

  /**
   * Color used for the checkbox fill and checkmark when checked.
   * @default ThemeColor.primary
   */
  color?: ThemeColor;

  /**
   * Color used for the focus ring. If not provided, uses `color` prop.
   */
  focusColor?: ThemeColor;

  /**
   * Color used for the focus ring when unchecked.
   * If null, falls back to `focusColor` or `color`.
   * @default ThemeColor.primary
   */
  focusColorUnchecked?: ThemeColor;

  /**
   * Color used for the checkbox border when unchecked (off state).
   * When not provided, defaults to a neutral gray.
   */
  offColor?: ThemeColor;

  /**
   * If true, renders a smaller compact version of the checkbox.
   * @default false
   */
  dense?: boolean;

  /**
   * Accessible label for the checkbox.
   */
  "aria-label"?: string;

  /**
   * ID of element that describes the checkbox.
   */
  "aria-describedby"?: string;
}

/**
 * A checkbox control that provides an on/off binary choice, rendered as a
 * checkmark rather than a toggle. Supports keyboard navigation, accessibility
 * features, and customizable colors. Available in standard and dense (compact) sizes.
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  onChange,
  disabled = false,
  className,
  id,
  color = ThemeColor.primary,
  offColor,
  focusColor,
  focusColorUnchecked = ThemeColor.primary,
  dense = false,
  ...ariaProps
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.checked);
    }
  };

  // Determine focus color based on checked state
  const currentFocusColor = checked
    ? focusColor || color
    : focusColorUnchecked || focusColor || color;
  const focusRingColor = getFocusRingStyles(currentFocusColor);

  // Size variants for dense mode
  const boxSize = dense ? "w-3.5 h-3.5" : "w-4 h-4";
  const checkmarkGeometry = dense
    ? "after:w-[3px] after:h-[6px] after:left-[4px] after:top-[1px]"
    : "after:w-1 after:h-2 after:left-[5px] after:top-[1.5px]";

  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={handleChange}
      disabled={disabled}
      id={id}
      className={cn(
        "relative rounded transition-all duration-200 appearance-none cursor-pointer",
        "shrink-0 border-2",
        boxSize,
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        focusRingColor,
        "after:content-[''] after:absolute after:hidden checked:after:block",
        "after:border-back-50 after:dark:border-back-50 after:border-r-2 after:border-b-2 after:rotate-45",
        checkmarkGeometry,
        checked
          ? cn(getSolidBackgroundStyles(color), "border-transparent")
          : offColor
            ? getBorderStyles(offColor)
            : "border-back-300 dark:border-back-600 bg-back-50 dark:bg-back-900",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      {...ariaProps}
    />
  );
};
